import React from 'react';
import { GripVertical, Type, TrendingUp, Eye, Plus, Layers, Star } from 'lucide-react';
import { TYPE_ICONS } from './constants';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getTooltipTheme } from '@/lib/tooltipTheme';

// Highlight occurrences of `term` within `text` (case-insensitive) using <mark>.
function highlightMatch(text, term) {
  const q = (term || '').trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-300/70 text-inherit rounded-[2px] px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export function DragField({ field, onBrowse, onAddToVisual, onViewData, onBin, highlight, onToggleFav, isFav }) {
  const isDim = field.semantic_type === 'dimension';
  const dt = (field.data_type || '').toLowerCase();
  const canBin = onBin && !field.is_calculated && (
    ['integer', 'float', 'number'].includes(dt) ||
    dt.includes('date') ||
    field.semantic_type === 'measure'
  );
  const Icon = TYPE_ICONS[field.data_type] || (isDim ? Type : TrendingUp);
  // Display: fields are grouped by table in the panel, so show the plain field
  // label (_baseLabel) without the redundant "Table · " prefix that business_name
  // carries for disambiguating column labels/exports elsewhere.
  const displayName = field._baseLabel || field.business_name || field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Theme-aware tooltip surface (soft near-white in light, lifted dark in dark).
  const tip = getTooltipTheme();

  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ name: field.name, type: field.semantic_type, entity_id: field.entity_id }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDoubleClick = () => {
    if (onAddToVisual) onAddToVisual(field.name, field.semantic_type, field.entity_id);
  };

  // Right-click → "View Data". Default: open the field-stats popup. We
  // intentionally still preventDefault even when no handler is wired so the
  // browser context menu doesn't pop up over a draggable field pill, which
  // would be a confusing UX in itself.
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onViewData) onViewData(field);
  };

  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>
        <div
          draggable
          onDragStart={handleDragStart}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing select-none transition-all text-[11px] hover:bg-muted/40 group/field ${isDim ? 'text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300' : 'text-primary hover:text-primary dark:text-primary/80'}`}
          data-testid={`drag-${field.name}`}
        >
          <GripVertical className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate flex-1 min-w-0">{highlightMatch(displayName, highlight)}</span>
          {field.aggregation && <span className="text-[8px] text-muted-foreground/70 flex-shrink-0">{field.aggregation}</span>}
          {onToggleFav && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFav(field); }}
              className={`flex-shrink-0 w-5 h-5 rounded hover:bg-amber-500/20 flex items-center justify-center transition-all ${isFav ? 'text-amber-400' : 'text-muted-foreground/60 hidden group-hover/field:flex hover:text-amber-400'}`}
              title={isFav ? 'Unpin from Favourites' : 'Pin to Favourites'}
              data-testid={`fav-${field.name}`}
            >
              <Star className={`w-3 h-3 ${isFav ? 'fill-amber-400' : ''}`} />
            </button>
          )}
          {onBrowse && <button onClick={(e) => { e.stopPropagation(); onBrowse(field); }} className="hidden group-hover/field:flex flex-shrink-0 w-5 h-5 rounded hover:bg-muted/50 text-muted-foreground/70 hover:text-foreground transition-all items-center justify-center"><Eye className="w-3 h-3" /></button>}
          {canBin && (
            <button
              onClick={(e) => { e.stopPropagation(); onBin(field); }}
              className="hidden group-hover/field:flex flex-shrink-0 w-5 h-5 rounded hover:bg-emerald-500/20 text-muted-foreground/70 hover:text-emerald-400 transition-all items-center justify-center"
              title="Bin this field into groups"
              data-testid={`bin-${field.name}`}
            >
              <Layers className="w-3 h-3" />
            </button>
          )}
          {onAddToVisual && (
            <button
              onClick={(e) => { e.stopPropagation(); onAddToVisual(field.name, field.semantic_type, field.entity_id); }}
              className="flex-shrink-0 w-5 h-5 rounded bg-blue-500/80 hover:bg-blue-500 text-white flex items-center justify-center transition-all shadow-sm"
              data-testid={`add-${field.name}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="start"
        sideOffset={6}
        avoidCollisions={false}
        style={{ backgroundColor: tip.bg, color: tip.fg, borderColor: tip.border }}
        className="max-w-[260px] border shadow-lg"
      >
        <div className="space-y-0.5">
          {field.description && <p className="text-xs" style={{ color: tip.fg }}>{field.description}</p>}
          <p className="text-[9px] font-mono" style={{ color: tip.sub }}>{field.name}</p>
          {field.entity_name && <p className="text-[9px]" style={{ color: tip.sub }}>{field.entity_logical_name || field.entity_name}</p>}
          <p className="text-[8px]" style={{ color: tip.hint }}>Double-click to add · Right-click to view data</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
